
import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getUserRole, isLoggedIn, logout, getAuthToken, getUserInfo } from "../../utils/auth";
import { CiLogin } from "react-icons/ci";
import { HiOutlineUserPlus } from "react-icons/hi2";
import SupportLogo from "../../assets/img/customer-service-center/support-logo.png";

const menuItems = [
  {
    key: "intro",
    label: "추모원 소개",
    path: "/ceo-greeting",
    subs: [
      { label: "대표이사 인사말", path: "/ceo-greeting" },
      { label: "소개", path: "/introduction-memorial" },
      { label: "시설안내", path: "/facilities" },
      { label: "추모원갤러리", path: "/memorial-gallery-list" },
      { label: "오시는길", path: "/directions" },
    ],
  },
  {
    key: "view",
    label: "둘러보기",
    path: "/overall-view",
    subs: [
      { label: "전체전경", path: "/overall-view" },
      { label: "하늘관", path: "/sky-hall" },

      { label: "하늘관 VIP", path: "/hall-vip" },

      { label: "별빛관", path: "/starlight-hall" },

      { label: "햇빛관", path: "/sunshine-hall" },
      { label: "S관", path: "/s-hall" },
      { label: "A관", path: "/a-hall" },
      { label: "가족묘", path: "/family-hall" },
      { label: "야외가족봉안당", path: "/outdoor-hall" },
    ],
  },
  {
    key: "guide",
    label: "분양안내",
    path: "/sky-special-room",
    subs: [
      { label: "분양가격", path: "/sky-special-room" },
      { label: "분양절차", path: "/sales-info" },
    ],
  },
  {
    key: "service",
    label: "상조의전",
    path: "/mutual-assistance-product-info",
    subs: [
      { label: "상품안내", path: "/mutual-assistance-product-info" },
      { label: "개장/이장", path: "/opening-relocation" },
      { label: "장례정보", path: "/general-funeral-procedures" },
    ],
  },
  {
    key: "memorial",
    label: "유족광장",
    path: "/cyber-memorial-search",
    subs: [
      { label: "사이버추모관", path: "/cyber-memorial-search" },
      { label: "하늘로 보내는 편지", path: "/letter-to-heaven-board-list" },
      { label: "관리비 납부 조회", path: "/management-fee-payment-inquiry" },
      { label: "지방 출력", path: "/ancestral-tablet" },
    ],
  },
  {
    key: "support",
    label: "고객센터",
    path: "/announcements",
    subs: [
      { label: "공지사항", path: "/announcements" },
      { label: "추모원 소식", path: "/memorial-news" },
      { label: "자주하는 질문", path: "/faq" },
      { label: "문의하기", path: "/contact-us" },
    ],
  },
];

function HeaderMenu() {
  const location = useLocation();
  const navigate = useNavigate();
  const isEditInfoPage = location.pathname === "/edit-info";

  const [loginStatus, setLoginStatus] = useState(isLoggedIn());
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [openIndex, setOpenIndex] = useState(-1);
  const [activeKey, setActiveKey] = useState("intro");
  const [clickedSubKey, setClickedSubKey] = useState("");

  const closeSubmenu = () => setOpenIndex(-1);

  useEffect(() => {
    setLoginStatus(isLoggedIn());
    const handleStorageChange = () => setLoginStatus(isLoggedIn());
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [location.pathname]);

  useEffect(() => setMobileMenuOpen(false), [location.pathname]);

  useEffect(() => {
    const found = menuItems.find((m) => {
      if (location.pathname.startsWith(m.path)) return true;
      return m.subs.some((s) => location.pathname.startsWith(s.path));
    });
    setActiveKey(found ? found.key : "");
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        mobileMenuOpen &&
        !e.target.closest(".mobile-nav-content") &&
        !e.target.closest(".mobile-menu-button")
      ) {
        setMobileMenuOpen(false);
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "unset";
    };
  }, [mobileMenuOpen]);

  const handleLogout = () => {
    logout();
    setLoginStatus(false);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => setMobileMenuOpen((s) => !s);

  const isActiveLink = (paths) => paths.some((p) => location.pathname.includes(p));

  // Handle admin link clicks - redirect through login page with token for authentication
  const handleAdminLinkClick = (e, adminUrl) => {
    e.preventDefault();
    const token = getAuthToken();
    const userInfo = getUserInfo();
    
    if (!token || !loginStatus) {
      window.open(adminUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    
    // Strategy: First go to admin login page with token
    // The login page should validate token and redirect to main page
    // If login page doesn't handle token, try direct access to target page with token
    
    const separator = '?';
    const tokenParams = [
      `token=${encodeURIComponent(token)}`,
      `authToken=${encodeURIComponent(token)}`,
      `accessToken=${encodeURIComponent(token)}`,
      `jwt=${encodeURIComponent(token)}`,
      `bearerToken=${encodeURIComponent(token)}`,
      `autoLogin=true`
    ].join('&');
    
    let userParam = '';
    if (userInfo) {
      if (userInfo.nickname) userParam += `&username=${encodeURIComponent(userInfo.nickname)}`;
      if (userInfo.userId) userParam += `&userId=${encodeURIComponent(userInfo.userId)}`;
      if (userInfo.role) userParam += `&role=${encodeURIComponent(userInfo.role)}`;
    }
    
    // First, try going through login page with token (login page should auto-authenticate and redirect)
    const loginUrl = `https://admin.prds.kr/admin/adm_login.jsp${separator}${tokenParams}${userParam}`;
    
    // Open login page - if backend is configured, it will auto-login and redirect to main page
    const newWindow = window.open(loginUrl, '_blank', 'noopener,noreferrer');
    
    // Also try direct access to target page with token as fallback
    // This will work if admin server checks token on the target page itself
    setTimeout(() => {
      const targetUrl = `${adminUrl}${adminUrl.includes('?') ? '&' : '?'}${tokenParams}${userParam}`;
      // If login page redirects to login again, try direct access
      // Note: This is a fallback - the login page method should work if backend is configured
      if (newWindow && !newWindow.closed) {
        // Check if we need to try direct access
        // (This would require checking the window location, but cross-origin restrictions prevent this)
        // So we'll just rely on the login page redirect method
      }
    }, 1000);
  };

  return (
    <header className="main-header-menu" onMouseLeave={() => setOpenIndex(-1)}>
      <div className="header-container">
        <div className="header-content">
          <div className="header-top">
            <div className="auth-buttons">
              {loginStatus ? (
                <>
                  <button onClick={handleLogout} className="auth-button">
                    <span>로그아웃</span>
                  </button>
                  {!isEditInfoPage && (
                    <Link to="/edit-info" className="auth-link">
                      <span>내정보 수정하기</span>
                    </Link>
                  )}

                  {/* ✅ Only show these links if user role is ADMIN */}
                  {getUserRole() === "ADMIN" && (
                    <>
                      <a 
                        href="https://admin.prds.kr/admin/adm_board_postit.jsp" 
                        className="auth-link" 
                        onClick={(e) => handleAdminLinkClick(e, "https://admin.prds.kr/admin/adm_board_postit.jsp")}
                      >
                        <span>포스트잇</span>
                      </a>
                      <a 
                        href="https://admin.prds.kr/admin/adm_board_note.jsp" 
                        className="auth-link" 
                        onClick={(e) => handleAdminLinkClick(e, "https://admin.prds.kr/admin/adm_board_note.jsp")}
                      >
                        <span>상담일지</span>
                      </a>
                      {/* <a href="http://gangwha-admin-live.dvconsulting.org/admin/adm_main.jsp" className="auth-link" target="_blank" rel="noopener noreferrer">
                        <span>관리자</span>
                      </a> */}
                      <a 
                        href="https://admin.prds.kr/admin/adm_main.jsp" 
                        className="auth-link" 
                        onClick={(e) => handleAdminLinkClick(e, "https://admin.prds.kr/admin/adm_main.jsp")}
                      >
                        <span>관리자</span>
                      </a>
                    </>
                  )}
                </>
              ) : (
                <>
                  <Link to="/signup" className="auth-link">
                    <HiOutlineUserPlus size={20} />
                    <span>회원가입</span>
                  </Link>
                  <Link to="/login" className="auth-link">
                    <CiLogin size={20} />
                    <span>로그인</span>
                  </Link>
                </>
              )}
            </div>
          </div>

          <div className="header-main">
            <div className="logo-section">
              <Link to="/" className="logo-link">
                <img
                  src="/images/Logo/header-logo.png"
                  alt="파라다이스 추모원 로고"
                  className="logo-image"
                />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <nav className="nav-section">
              <ul className="nav-list">
                {menuItems.map((item, idx) => {
                  const active = isActiveLink([item.path, ...item.subs.map((s) => s.path)]);
                  return (
                    <li
                      key={item.key}
                      className={`nav-item ${active ? "active" : ""}`}
                      onMouseEnter={() => setOpenIndex(idx)}
                    //  onMouseLeave={() => setOpenIndex(-1)}

                    >
                      <Link to={item.path} className={`nav-link ${active ? "active" : ""}`}
                        onClick={closeSubmenu}>
                        {item.label}
                      </Link>
                      {/* {active && <div className="nav-underline" />} */}
                    </li>
                  );
                })}
              </ul>
            </nav>

            {/* Mobile menu button */}
            <button
              className={`mobile-menu-button ${mobileMenuOpen ? "active" : ""}`}
              onClick={toggleMobileMenu}
              aria-label="Toggle mobile menu"
            >
              <div className="hamburger-line" />
              <div className="hamburger-line" />
              <div className="hamburger-line" />
            </button>
          </div>
        </div>
      </div>
      {/* Mobile Navigation Overlay */}
      <div className={`mobile-nav-overlay ${mobileMenuOpen ? 'active' : ''}`}>
        <div className={`mobile-nav-content ${mobileMenuOpen ? 'active' : ''}`}>
          {/* Mobile Nav Header */}
          <div className="mobile-nav-header">
            <img
              src="/images/Logo/header-logo.png"
              alt="파라다이스 추모원 로고"
              className="mobile-logo"
              onClick={() => {
                navigate('/');
              }}
            />
            <button
              className="close-button"
              onClick={closeMobileMenu}
              aria-label="Close mobile menu"
            >
              ×
            </button>
          </div>

          {/* Mobile Navigation List */}
          <ul className="mobile-nav-list">
            <li className="mobile-nav-item">
              <Link
                to="/ceo-greeting"
                className={`mobile-nav-link ${isActiveLink(['/ceo-greeting']) ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                추모원 소개
              </Link>
            </li>
            <li className="mobile-nav-item">
              <Link
                to="/overall-view"
                className={`mobile-nav-link ${isActiveLink(['/overall-view']) ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                둘러보기
              </Link>
            </li>
            <li className="mobile-nav-item">
              <Link
                to="/sky-special-room"
                className={`mobile-nav-link ${isActiveLink(['/sky-special-room', '/sales-info', '/outdoor-hall-sales',
                  '/sky-hall-vip-sales', '/sky-hall-phoenix-room', '/star-hall-sales', '/sunlight-hall-sales', '/s-hall-sales']) ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                분양안내
              </Link>
            </li>
            <li className="mobile-nav-item">
              <Link
                to="/mutual-assistance-product-info"
                className={`mobile-nav-link ${isActiveLink(['/mutual-assistance-product-info', '/general-funeral-procedures',
                  '/funeral-information-religious', '/funeral-methods', '/funeral-information-other',
                  '/event-procedure', '/opening-relocation', '/service-advantages']) ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                상조의전
              </Link>
            </li>
            <li className="mobile-nav-item">
              <Link
                to="/cyber-memorial-search"
                className={`mobile-nav-link ${isActiveLink(['/cyber-memorial-search']) ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                유족광장
              </Link>
            </li>
            <li className="mobile-nav-item">
              <Link
                to="/announcements"
                className={`mobile-nav-link ${isActiveLink(['/announcements', '/memorial-news', '/contact-us', '/faq']) ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                고객센터
              </Link>
            </li>
          </ul>

          {/* Mobile Auth Section */}
          <div className="mobile-auth-section">
            <div className="mobile-auth-buttons">
              {loginStatus ? (
                // Logged in user options
                <>
                  <button
                    onClick={() => {
                      handleLogout();
                      closeMobileMenu();
                    }}
                    className="mobile-auth-button"
                  >
                    <span>로그아웃</span>
                  </button>
                  {!isEditInfoPage && (
                    <Link
                      to="/edit-info"
                      className="mobile-auth-link"
                      onClick={closeMobileMenu}
                    >
                      <span>내정보 수정하기</span>
                    </Link>
                  )}
                  {/* ✅ Only show these links if user role is ADMIN */}
                  {getUserRole() === "ADMIN" && (
                    <>
                      <a 
                        href="https://admin.prds.kr/admin/adm_board_postit.jsp" 
                        className="auth-link" 
                        onClick={(e) => handleAdminLinkClick(e, "https://admin.prds.kr/admin/adm_board_postit.jsp")}
                      >
                        <span>포스트잇</span>
                      </a>
                      <a 
                        href="https://admin.prds.kr/admin/adm_board_note.jsp" 
                        className="auth-link" 
                        onClick={(e) => handleAdminLinkClick(e, "https://admin.prds.kr/admin/adm_board_note.jsp")}
                      >
                        <span>상담일지</span>
                      </a>
                      {/* <a href="http://www.prds.kr/admin/adm_main.jsp" className="auth-link" target="_blank" rel="noopener noreferrer">
                        <span>관리자</span>
                      </a> */}
                      <a 
                        href="https://admin.prds.kr/admin/adm_main.jsp" 
                        className="auth-link" 
                        onClick={(e) => handleAdminLinkClick(e, "https://admin.prds.kr/admin/adm_main.jsp")}
                      >
                        <span>관리자</span>
                      </a>
                    </>
                  )}
                </>
              ) : (
                // Not logged in user options
                <>
                  <Link
                    to="/signup"
                    className="mobile-auth-link"
                    onClick={closeMobileMenu}
                  >
                    <HiOutlineUserPlus size={20} />
                    <span>회원가입</span>
                  </Link>
                  <Link
                    to="/login"
                    className="mobile-auth-link"
                    onClick={closeMobileMenu}
                  >
                    <CiLogin size={20} />
                    <span>로그인</span>
                  </Link>
                </>
              )}
            </div>
          </div>

          {/* Consultation Section */}
          <div className="consultation-section">
            <div className="consultation-content">
              <h3 className="consultation-title">분양상담</h3>
              <p className="consultation-description">
                가족분들의 마음의 안식처가 되는 추모의 공간입니다.
              </p>
              <div className="consultation-phone">
                <a href="tel:1599-9442" className="phone-link">1599-9442</a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {openIndex >= 0 && (
        <div
          className="mega-menu fade-in"
          onMouseEnter={() => setOpenIndex(openIndex)}
          onMouseLeave={() => setOpenIndex(-1)}
        >

          <div className="mega-menu-content">
            <div className="left-side">
              <div className="consult-box">
                <h3>분양상담</h3>
                <p>가족분들의 마음의 안식처가 되는 추모의 공간입니다.</p>
                <div className="phone-section">
                  <img src={SupportLogo} alt="logo" className="logo" />
                  <a href="tel:1599-9442" className="phone">
                    1599-9442
                  </a>
                </div>
              </div>
            </div>
            <div className="submenu-columns-container">
              <div className="submenu-columns" role="menu">
                {menuItems.map((item) => (
                  <div
                    key={item.key}
                    className={`submenu-col ${activeKey === item.key ? "highlight" : ""}`}
                  >
                    {item.subs.map((sub) => {
                      const isClicked = clickedSubKey === sub.path;
                      return (
                        <Link
                          key={sub.path}
                          to={sub.path}
                          className={`submenu-link ${isClicked ? "clicked" : ""} ${location.pathname === sub.path ? "active" : ""}`}
                          // onClick={() => {
                          //   setClickedSubKey(sub.path);
                          //   setActiveKey(item.key);
                          // }}
                          onClick={() => {
                            setActiveKey(item.key);
                            closeSubmenu(); 
                          }}
                        >
                          {sub.label}
                        </Link>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

export default HeaderMenu;

